import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { Tree, TreeNode } from 'react-organizational-chart';
import html2canvas from 'html2canvas';
import camelCaseRecursive from 'camelcase-keys-recursive';

import organizationChartRequest from '../../requests/organizationChart';
import { requestCurrentCompany } from '../../actions/auth';
import { Icon } from '../../components';
import OrganizationNode from './OrganizationNode';
import './OrganizationChartReact.scss';

const OrganizationChartReact = () => {
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loadedNodes, setLoadedNodes] = useState(new Set());
  const [childrenMap, setChildrenMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.8);
  const [isPanning, setIsPanning] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });

  const chartContainerRef = useRef(null);
  const wrapperRef = useRef(null);

  const { company } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const loadChildren = useCallback(
    (node, callback) => {
      const nodeId = node.id;

      if (loadedNodes.has(nodeId)) {
        callback();
        return;
      }

      if (node.children && node.children.length > 0) {
        const formattedChildren = node.children.map(child => {
          const childCount = child.childrenCount || 0;
          return {
            id: child.id,
            name: child.name || child.businessName,
            title: child.title,
            imageUrl: child.fileInfo?.fileUrl || child.businessLogo?.fileUrl,
            hasChildren: child.hasChildren || childCount > 0,
            childrenCount: childCount,
            isUser: child.isUser,
            isBusiness: child.isBusiness,
            businessName: child.businessName,
            ...child
          };
        });

        setChildrenMap(prev => ({
          ...prev,
          [nodeId]: formattedChildren
        }));

        setLoadedNodes(prev => new Set([...prev, nodeId]));
        callback();
        return;
      }

      if (node.childrenCount > 0) {
        organizationChartRequest({
          dispatch,
          params: { tree_data_from_id: nodeId, depth: '1', sort_column: 'name' },
          successCallback: response => {
            const { data } = response.data;
            const camelData = camelCaseRecursive(data);

            const formattedChildren = camelData.map(child => {
              const childCount = child.childrenCount || 0;
              return {
                id: child.id,
                name: child.name || child.businessName,
                title: child.title,
                imageUrl: child.fileInfo?.fileUrl || child.businessLogo?.fileUrl,
                hasChildren: child.hasChildren || childCount > 0,
                childrenCount: childCount,
                isUser: child.isUser,
                isBusiness: child.isBusiness,
                businessName: child.businessName,
                children: child.children || [],
                ...child
              };
            });

            setChildrenMap(prev => ({
              ...prev,
              [nodeId]: formattedChildren
            }));

            setLoadedNodes(prev => new Set([...prev, nodeId]));
            callback();
          }
        });
      } else {
        callback();
      }
    },
    [dispatch, loadedNodes]
  );

  const handleNodeExpand = useCallback(
    node => {
      const nodeId = node.id;
      const isCurrentlyExpanded = expandedNodes.has(nodeId);

      if (!isCurrentlyExpanded) {
        if (!loadedNodes.has(nodeId)) {
          setIsLoading(true);
          loadChildren(node, () => {
            setExpandedNodes(prev => new Set([...prev, nodeId]));
            setIsLoading(false);
          });
        } else {
          setExpandedNodes(prev => new Set([...prev, nodeId]));
        }
      } else {
        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(nodeId);
          return newSet;
        });
      }
    },
    [expandedNodes, loadedNodes, loadChildren]
  );

  const renderTree = useCallback(
    node => {
      if (!node) return null;

      const isExpanded = expandedNodes.has(node.id);
      const children = childrenMap[node.id] || [];
      const shouldShowChildren = isExpanded && children.length > 0;

      return (
        <TreeNode
          key={node.id}
          label={<OrganizationNode node={node} onExpand={handleNodeExpand} isExpanded={isExpanded} />}
        >
          {shouldShowChildren && children.map(child => renderTree(child))}
        </TreeNode>
      );
    },
    [expandedNodes, childrenMap, handleNodeExpand]
  );

  const initializeTree = useCallback(() => {
    organizationChartRequest({
      dispatch,
      params: { depth: '1', sort_column: 'name' },
      successCallback: response => {
        const { data } = response.data;
        const employeesData = camelCaseRecursive(data);
        const companyData = company && company.id ? company : { id: 0, businessName: 'Empresa' };

        const hasEmployees = employeesData && employeesData.length > 0;
        const employeesCount = hasEmployees ? employeesData.length : 0;

        const rootNode = {
          ...companyData,
          id: companyData.id,
          name: companyData.businessName,
          isBusiness: true,
          hasChildren: hasEmployees,
          childrenCount: employeesCount,
          children: employeesData
        };

        setTreeData(rootNode);

        if (hasEmployees) {
          const formattedChildren = employeesData.map(child => {
            const childCount = child.childrenCount || 0;
            return {
              id: child.id,
              name: child.name || child.businessName,
              title: child.title,
              imageUrl: child.fileInfo?.fileUrl || child.businessLogo?.fileUrl,
              hasChildren: child.hasChildren || childCount > 0,
              childrenCount: childCount,
              isUser: child.isUser,
              isBusiness: child.isBusiness,
              businessName: child.businessName,
              children: child.children || [],
              ...child
            };
          });

          setChildrenMap(prev => ({
            ...prev,
            [companyData.id]: formattedChildren
          }));

          setLoadedNodes(prev => new Set([...prev, companyData.id]));
        }
      }
    });
  }, [dispatch, company]);

  const expandAll = useCallback(() => {
    organizationChartRequest({
      dispatch,
      params: { depth: '99999', sort_column: 'name' },
      successCallback: response => {
        const { data } = response.data;
        const employeesData = camelCaseRecursive(data);

        const buildChildrenMap = (nodes, parentId = null) => {
          const map = {};
          const nodesByParent = {};

          const flattenNodes = (nodesList, pId) => {
            nodesList.forEach(node => {
              if (!nodesByParent[pId]) {
                nodesByParent[pId] = [];
              }

              const childCount = node.childrenCount || (node.children ? node.children.length : 0);
              const formattedNode = {
                id: node.id,
                name: node.name || node.businessName,
                title: node.title,
                imageUrl: node.fileInfo?.fileUrl || node.businessLogo?.fileUrl,
                hasChildren: node.hasChildren || childCount > 0,
                childrenCount: childCount,
                isUser: node.isUser,
                isBusiness: node.isBusiness,
                businessName: node.businessName,
                children: node.children || []
              };

              nodesByParent[pId].push(formattedNode);

              if (node.children && node.children.length > 0) {
                flattenNodes(node.children, node.id);
              }
            });
          };

          flattenNodes(nodes, parentId);

          Object.keys(nodesByParent).forEach(key => {
            map[key] = nodesByParent[key];
          });

          return map;
        };

        const newChildrenMap = buildChildrenMap(employeesData, treeData.id);
        setChildrenMap(prev => ({ ...prev, ...newChildrenMap }));

        const allNodeIds = new Set();
        const collectNodeIds = nodes => {
          nodes.forEach(node => {
            allNodeIds.add(node.id);
            if (node.children && node.children.length > 0) {
              collectNodeIds(node.children);
            }
          });
        };
        collectNodeIds(employeesData);
        allNodeIds.add(treeData.id);

        setExpandedNodes(allNodeIds);
        setLoadedNodes(allNodeIds);
      }
    });
  }, [dispatch, treeData]);

  const findMe = useCallback(() => {
    expandAll();
  }, [expandAll]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    if (expandedNodes.size === 0 && treeData) {
      setExpandedNodes(new Set([treeData.id]));
    }
  }, [expandedNodes, treeData]);

  const exportChart = useCallback(() => {
    if (!chartContainerRef.current) return;

    setIsLoading(true);

    const element = chartContainerRef.current;
    const originalTransform = element.style.transform;
    element.style.transform = 'scale(1)';

    html2canvas(element, {
      backgroundColor: '#f8f9fa',
      scale: 2,
      logging: false,
      useCORS: true,
      width: element.scrollWidth,
      height: element.scrollHeight
    })
      .then(canvas => {
        element.style.transform = originalTransform;

        const link = document.createElement('a');
        link.download = `organigrama-${new Date().getTime()}.png`;
        link.href = canvas.toDataURL();
        link.click();

        setIsLoading(false);
      })
      .catch(() => {
        element.style.transform = originalTransform;
        setIsLoading(false);
      });
  }, []);

  const exportChartAll = useCallback(() => {
    if (!treeData) return;

    setIsLoading(true);

    const previousExpandedNodes = new Set(expandedNodes);

    organizationChartRequest({
      dispatch,
      params: { depth: '99999', sort_column: 'name' },
      successCallback: response => {
        const { data } = response.data;
        const employeesData = camelCaseRecursive(data);

        const buildChildrenMap = (nodes, parentId = null) => {
          const map = {};
          const nodesByParent = {};

          const flattenNodes = (nodesList, pId) => {
            nodesList.forEach(node => {
              if (!nodesByParent[pId]) {
                nodesByParent[pId] = [];
              }

              const childCount = node.childrenCount || (node.children ? node.children.length : 0);
              const formattedNode = {
                id: node.id,
                name: node.name || node.businessName,
                title: node.title,
                imageUrl: node.fileInfo?.fileUrl || node.businessLogo?.fileUrl,
                hasChildren: node.hasChildren || childCount > 0,
                childrenCount: childCount,
                isUser: node.isUser,
                isBusiness: node.isBusiness,
                businessName: node.businessName,
                children: node.children || []
              };

              nodesByParent[pId].push(formattedNode);

              if (node.children && node.children.length > 0) {
                flattenNodes(node.children, node.id);
              }
            });
          };

          flattenNodes(nodes, parentId);

          Object.keys(nodesByParent).forEach(key => {
            map[key] = nodesByParent[key];
          });

          return map;
        };

        const newChildrenMap = buildChildrenMap(employeesData, treeData.id);
        setChildrenMap(prev => ({ ...prev, ...newChildrenMap }));

        const allNodeIds = new Set();
        const collectNodeIds = nodes => {
          nodes.forEach(node => {
            allNodeIds.add(node.id);
            if (node.children && node.children.length > 0) {
              collectNodeIds(node.children);
            }
          });
        };
        collectNodeIds(employeesData);
        allNodeIds.add(treeData.id);

        setExpandedNodes(allNodeIds);
        setLoadedNodes(allNodeIds);

        setTimeout(() => {
          if (chartContainerRef.current) {
            const element = chartContainerRef.current;
            const originalTransform = element.style.transform;
            element.style.transform = 'scale(1)';

            html2canvas(element, {
              backgroundColor: '#f8f9fa',
              scale: 2,
              logging: false,
              useCORS: true,
              width: element.scrollWidth,
              height: element.scrollHeight
            })
              .then(canvas => {
                element.style.transform = originalTransform;

                const link = document.createElement('a');
                link.download = `organigrama-completo-${new Date().getTime()}.png`;
                link.href = canvas.toDataURL();
                link.click();

                setExpandedNodes(previousExpandedNodes);
                setIsLoading(false);
              })
              .catch(() => {
                element.style.transform = originalTransform;
                setExpandedNodes(previousExpandedNodes);
                setIsLoading(false);
              });
          }
        }, 1000);
      }
    });
  }, [treeData, expandedNodes, dispatch]);

  const handleMouseDown = useCallback(e => {
    if (!wrapperRef.current) return;

    setIsPanning(true);
    const wrapper = wrapperRef.current;
    setStartPos({
      x: e.clientX,
      y: e.clientY
    });
    setScrollPos({
      x: wrapper.scrollLeft,
      y: wrapper.scrollTop
    });

    wrapper.style.cursor = 'grabbing';
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    e => {
      if (!isPanning || !wrapperRef.current) return;

      const wrapper = wrapperRef.current;
      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;

      wrapper.scrollLeft = scrollPos.x - dx;
      wrapper.scrollTop = scrollPos.y - dy;

      e.preventDefault();
    },
    [isPanning, startPos, scrollPos]
  );

  const handleMouseUp = useCallback(
    e => {
      if (!isPanning || !wrapperRef.current) return;

      setIsPanning(false);
      const wrapper = wrapperRef.current;
      wrapper.style.cursor = 'grab';

      e.preventDefault();
    },
    [isPanning]
  );

  const handleMouseLeave = useCallback(() => {
    if (isPanning && wrapperRef.current) {
      setIsPanning(false);
      wrapperRef.current.style.cursor = 'grab';
    }
  }, [isPanning]);

  useEffect(() => {
    if (!company || !company.id) {
      dispatch(requestCurrentCompany());
    }
  }, [dispatch, company]);

  useEffect(() => {
    if (company && company.id && !treeData) {
      initializeTree();
    }
  }, [company, treeData, initializeTree]);

  if (!treeData) {
    return (
      <div className="loading-overlay">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const getCursor = () => {
    if (isPanning) return 'grabbing';
    return 'grab';
  };

  return (
    <>
      <Row className="mt-4 mb-4">
        <Col>
          <Row className="align-items-center justify-content-between">
            <Col>
              <h2 className="mb-3 mt-3">Organigrama</h2>
            </Col>
            <Row className="mr-3 mt-3 mb-3">
              <Button variant="info" className="text-uppercase" onClick={exportChart} disabled={isLoading}>
                Descargar
              </Button>
              <Button variant="info" className="text-uppercase ml-2" onClick={exportChartAll} disabled={isLoading}>
                Descargar todo
              </Button>
              <Button variant="info" className="text-uppercase ml-2" onClick={findMe} disabled={isLoading}>
                Encuéntrame
              </Button>
            </Row>
          </Row>
        </Col>
      </Row>
      <div
        role="button"
        tabIndex={-1}
        aria-label="Área de navegación del organigrama"
        ref={wrapperRef}
        className="org-chart-react-wrapper pan-mode"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: getCursor(),
          userSelect: isPanning ? 'none' : 'auto'
        }}
      >
        {isLoading && (
          <div className="loading-overlay">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        <div className="chart-tools-container">
          <Icon color="white" className="zoom-button" icon="plus" onClick={zoomIn} />
          <Icon color="white" className="zoom-button" icon="minus" onClick={zoomOut} />
        </div>
        <div className="chart-tools-container reset-tool">
          <Icon color="white" className="zoom-button" icon="enlarge" onClick={resetZoom} />
        </div>
        <div ref={chartContainerRef} className="org-chart-react-container" style={{ transform: `scale(${zoomLevel})` }}>
          <Tree
            lineWidth="2px"
            lineColor="#797979"
            lineBorderRadius="10px"
            label={
              <OrganizationNode
                node={treeData}
                onExpand={handleNodeExpand}
                isExpanded={expandedNodes.has(treeData.id)}
              />
            }
          >
            {expandedNodes.has(treeData.id) &&
              childrenMap[treeData.id] &&
              childrenMap[treeData.id].map(child => renderTree(child))}
          </Tree>
        </div>
      </div>
    </>
  );
};

export default OrganizationChartReact;
